import React, { useEffect, useState } from 'react';
import { moneyMarketApi, counterpartyApi } from '../services/api';
import DocumentUpload from '../components/DocumentUpload';
import DocumentList from '../components/DocumentList';
import DealSlipViewModal from '../components/DealSlipViewModal';
import '../styles/MoneyMarketBlotter.css';

export default function FrontOfficeMoneyMarketBlotter() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [counterparties, setCounterparties] = useState([]);
  const [rejectModal, setRejectModal] = useState({ open: false, deal: null, comment: '' });
  const [viewModal, setViewModal] = useState({ open: false, deal: null });
  const [documentDeal, setDocumentDeal] = useState(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = user.role;

  useEffect(() => {
    setLoading(true);
    Promise.all([
      moneyMarketApi.getAll({ limit: 100 }), // Get more to filter properly
      counterpartyApi.getAll()
    ])
      .then(([allDeals, cps]) => {
        // Filter for front office approval level (current_approval_level = 'front_office' or 1) and pending status
        const filtered = (allDeals || []).filter(deal => 
          (deal.current_approval_level === 'front_office' || deal.current_approval_level === 1) && 
          deal.status === 'pending'
        );
        setDeals(filtered);
        setCounterparties(cps);
      })
      .catch(() => setError('Failed to fetch deals'))
      .finally(() => setLoading(false));
  }, []);

  function getCounterpartyName(id) {
    const cp = counterparties.find(c => c.unique_id === id || c.id === id);
    return cp ? (cp.short_name || cp.name || id) : id;
  }

  function formatCurrency(amount) {
    if (!amount) return '0.00';
    return parseFloat(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function getStatusBadgeClass(status) {
    switch(status) {
      case 'pending': return 'status-badge status-pending';
      case 'approved': return 'status-badge status-approved';
      case 'rejected': return 'status-badge status-rejected';
      default: return 'status-badge status-default';
    }
  }

  function getApprovalLevelBadgeClass(level) {
    if (!level) return 'approval-level-badge';
    return `approval-level-badge approval-level-${level}`;
  }

  function canAuthorize(deal) {
    // Only show actions if user is at the current approval level and status is pending
    return (
      deal.status === 'pending' &&
      (
        ((userRole === 'front_office' || userRole === 'front_office_verifier') && 
         (deal.current_approval_level === 'front_office' || deal.current_approval_level === 1)) ||
        (userRole === 'back_office_verifier' && 
         (deal.current_approval_level === 'back_office_verifier' || deal.current_approval_level === 2)) ||
        (userRole === 'back_office_final' && 
         (deal.current_approval_level === 'back_office_final' || deal.current_approval_level === 3))
      )
    );
  }

  async function handleApprove(deal) {
    let nextLevel = null;
    let newStatus = 'pending';
    if (deal.current_approval_level === 'front_office' || deal.current_approval_level === 1) {
      nextLevel = 'back_office_verifier';
    } else if (deal.current_approval_level === 'back_office_verifier' || deal.current_approval_level === 2) {
      nextLevel = 'back_office_final';
    } else if (deal.current_approval_level === 'back_office_final' || deal.current_approval_level === 3) {
      nextLevel = 'back_office_final';
      newStatus = 'approved';
    }
    await moneyMarketApi.update(deal.deal_number, {
      status: newStatus,
      current_approval_level: nextLevel,
      authorized_by: user.username,
      authorized_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
    });
    // Refresh with filtered deals
    const allDeals = await moneyMarketApi.getAll({ limit: 100 });
    const filtered = (allDeals || []).filter(d => 
      (d.current_approval_level === 'front_office' || d.current_approval_level === 1) && 
      d.status === 'pending'
    );
    setDeals(filtered);
  }

  function openRejectModal(deal) {
    setRejectModal({ open: true, deal, comment: '' });
  }

  async function handleReject() {
    const { deal, comment } = rejectModal;
    if (!comment.trim()) return;
    await moneyMarketApi.update(deal.deal_number, {
      status: 'rejected',
      comment,
      current_approval_level: 'front_office', // Reset to front office so it goes back to creator
      authorized_by: user.username,
      authorized_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
    });
    setRejectModal({ open: false, deal: null, comment: '' });
    // Refresh
    const allDeals = await moneyMarketApi.getAll({ limit: 100 });
    const filtered = (allDeals || []).filter(deal => 
      (deal.current_approval_level === 'front_office' || deal.current_approval_level === 1) && 
      deal.status === 'pending'
    );
    setDeals(filtered);
  }

  function openViewModal(deal) {
    setViewModal({ open: true, deal });
  }

  function closeViewModal() {
    setViewModal({ open: false, deal: null });
  }

  const refreshDeals = async () => {
    setLoading(true);
    setError('');
    try {
      const allDeals = await moneyMarketApi.getAll({ limit: 100 });
      const filtered = (allDeals || []).filter(deal => 
        (deal.current_approval_level === 'front_office' || deal.current_approval_level === 1) && 
        deal.status === 'pending'
      );
      setDeals(filtered);
    } catch (err) {
      setError('Failed to refresh deals');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="money-market-blotter">
      <div className="blotter-header">
        <h2>Front Office Money Market Blotter</h2>
        <div className="blotter-actions">
          <button className="refresh-button" onClick={refreshDeals}>Refresh</button>
        </div>
      </div>
      
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading deals...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <div className="error-message">{error}</div>
          <button className="retry-button" onClick={() => window.location.reload()}>Retry</button>
        </div>
      ) : deals.length === 0 ? (
        <div className="no-data-container">
          <p>No transactions pending front office approval.</p>
        </div>
      ) : (
        <div className="blotter-table-container">
          <table className="blotter-table">
            <thead>
              <tr>
                <th>Deal Number</th>
                <th>Trade Date</th>
                <th>Value Date</th>
                <th>Counterparty</th>
                <th>Product Type</th>
                <th>Principal</th>
                <th>Status</th>
                <th>Approval Level</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {deals.map(deal => (
                <tr key={deal.deal_number}>
                  <td className="deal-id">{deal.deal_number}</td>
                  <td className="date">{deal.trade_date || ''}</td>
                  <td className="date">{deal.value_date || ''}</td>
                  <td className="counterparty">{getCounterpartyName(deal.counterparty_id)}</td>
                  <td className="deal-type">{deal.product_type}</td>
                  <td className="amount">{formatCurrency(deal.principal_amount)} {deal.currency || ''}</td>
                  <td className="status">
                    <span className={getStatusBadgeClass(deal.status || 'pending')}>
                      {deal.status || 'pending'}
                    </span>
                  </td>
                  <td className="status">
                    <span className={getApprovalLevelBadgeClass(deal.current_approval_level)}>
                      {deal.current_approval_level || 'front_office'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="view-details-btn" onClick={() => openViewModal(deal)}>View</button>
                      <button 
                        className="view-details-btn" 
                        onClick={() => setDocumentDeal(deal)}
                        style={{ background: '#6c757d' }}
                        title="View/Upload Documents"
                      >
                        📎 Docs
                      </button>
                      {canAuthorize(deal) && (
                        <>
                          <button className="approve-btn" onClick={() => handleApprove(deal)}>Approve</button>
                          <button className="reject-btn" onClick={() => openRejectModal(deal)}>Reject</button>
                        </>
                      )}
                    </div>
                    {deal.status === 'rejected' && deal.comment && (
                      <div style={{color:'#ef4444', fontSize:'0.75rem', marginTop:4}}>Reason: {deal.comment}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rejectModal.open && (
        <div className="modal-overlay" onClick={() => setRejectModal({ open: false, deal: null, comment: '' })}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Reject Deal</h3>
            <div style={{marginBottom: '1rem'}}>
              <p style={{color: '#666', fontSize: '0.9rem'}}>
                Deal Number: <strong>{rejectModal.deal?.deal_number}</strong>
              </p>
            </div>
            <textarea
              rows={4}
              placeholder="Enter rejection reason/message"
              value={rejectModal.comment}
              onChange={e => setRejectModal({ ...rejectModal, comment: e.target.value })}
            />
            <div className="modal-actions">
              <button 
                className="modal-button modal-button-cancel" 
                onClick={() => setRejectModal({ open: false, deal: null, comment: '' })}
              >
                Cancel
              </button>
              <button 
                className="modal-button modal-button-primary" 
                onClick={handleReject}
                disabled={!rejectModal.comment.trim()}
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
      {viewModal.open && (
        <DealSlipViewModal
          dealType="MoneyMarket"
          transaction={{
            ...viewModal.deal,
            counterparty_name: getCounterpartyName(viewModal.deal.counterparty_id),
          }}
          onClose={closeViewModal}
        />
      )}
      
      {documentDeal && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          zIndex: 1000, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{ 
            background: '#fff', 
            borderRadius: '8px', 
            padding: '24px', 
            maxWidth: '800px', 
            width: '100%', 
            maxHeight: '90vh', 
            overflow: 'auto' 
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>
                Documents - Deal #: {documentDeal.deal_number || documentDeal.id}
              </h3>
              <button 
                onClick={() => setDocumentDeal(null)}
                style={{ 
                  padding: '8px 16px', 
                  background: '#6c757d', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: 4, 
                  cursor: 'pointer' 
                }}
              >
                Close
              </button>
            </div>
            <DocumentUpload
              transactionType="money_market"
              transactionId={documentDeal.deal_number || documentDeal.id}
              onUploadSuccess={() => {}}
              onUploadError={(error) => console.error('Upload error:', error)}
              multiple={true}
            />
            <DocumentList
              transactionType="money_market"
              transactionId={documentDeal.deal_number || documentDeal.id}
              onDocumentDeleted={() => {}}
            />
          </div>
        </div>
      )}
    </div>
  );
}
